import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import styles from './InterviewHeightWeight.module.css';
import { Collapse } from '@mui/material';
import Footer from '../.shared/footer/footer';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import Header from '../.shared/header/header';

export default function InterviewHeightWeight() {
    const navigate = useNavigate();

    const { updateMultipleFields, interviewData } = useInterview();
    const [heightFeet, setHeightFeet] = useState('');
    const [heightInches, setHeightInches] = useState('');
    const [weight, setWeight] = useState('');
    const [bmi, setBmi] = useState(null);
    const [bmiCategory, setBmiCategory] = useState('');

    const validateFeet = (value) => {
        const num = parseInt(value);
        return !isNaN(num) && num >= 1 && num <= 8;
    }

    const validateInches = (value) => {
        const num = parseInt(value);
        return !isNaN(num) && num >= 0 && num <= 11;
    }

    const validateWeight = (value) => {
        const num = parseInt(value);
        return !isNaN(num) && num >= 1 && num <= 1000;
    }

    // Populate from localStorage on component mount
    useEffect(() => {
        
        // Track when user reaches the height-weight step
        trackPostHogEvent('interview_step_viewed', {
            interview_step: 'height-weight',
            step_number: 2,
            has_existing_data: !!(interviewData.heightFeet && interviewData.heightInches && interviewData.weight)
        });
        
        // Populate form from localStorage
        if (interviewData.heightFeet) {
            setHeightFeet(interviewData.heightFeet);
        }
        if (interviewData.heightInches) {
            setHeightInches(interviewData.heightInches);
        }
        if (interviewData.weight) {
            setWeight(interviewData.weight);
        }
    }, [interviewData]);

    useEffect(() => {
            if (heightFeet && heightInches && weight) {

                if (!validateFeet(heightFeet) || !validateInches(heightInches) || !validateWeight(weight)) {
                    setBmi(null);
                    setBmiCategory('');
                    return;
                }

            const totalInches = parseInt(heightFeet) * 12 + parseInt(heightInches);
            const heightInMeters = totalInches * 0.0254;
            const weightInKg = parseFloat(weight) * 0.453592;
            const calculatedBMI = weightInKg / (heightInMeters * heightInMeters);
            
            setBmi(calculatedBMI.toFixed(1));
            
            // Set BMI category
            if (calculatedBMI < 18.5) {
                setBmiCategory('Underweight - may not qualify for our programs');
            } else if (calculatedBMI >= 18.5 && calculatedBMI < 25) {
                setBmiCategory('Normal weight - may not qualify for standard programs');
            } else if (calculatedBMI >= 25 && calculatedBMI < 27) {
                setBmiCategory('Modified program available - limited dose options');
            } else {
                setBmiCategory('Qualifies for standard program - all dose options available');
            }
        } else {
            setBmi(null);
            setBmiCategory('');
        }
    }, [heightFeet, heightInches, weight]);

    const handleConfirm = (e) => {
        e.preventDefault();
        if (heightFeet && heightInches && weight && bmi) {
            // Save to interview context
            const dataToSave = {
                heightFeet: heightFeet,
                heightInches: heightInches,
                weight: weight,
                bmi: bmi,
                bmiCategory: bmiCategory
            };
            updateMultipleFields(dataToSave);
            
            // Track height/weight submission
            trackPostHogEvent('interview_height_weight_submitted', {
                height_feet: heightFeet,
                height_inches: heightInches,
                weight_lbs: weight,
                calculated_bmi: bmi,
                bmi_category: bmiCategory,
                interview_step: 'height-weight',
                step_number: 2
            });
            
            navigate('/interview/priority');
        }
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(-1);
    };

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header />
                <ProgressBar progress={75} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.heightWeightContent}>
                        <h1 className={styles.title}>
                            What is your height and weight?
                        </h1>

                        <span className={styles.subtitle}>This helps us calculate your BMI and provide personalized recommendations.</span>

                        <form className={styles.heightWeightForm}>

                            <div className={styles.formFields}>
                                <div className={styles.inputGroup}>

                                    <label className={styles.label}>Height</label>

                                    <div className={styles.heightInputsContainer}>

                                        <div className={styles.inputWrapper}>
                                            <input
                                                type="number"
                                                value={heightFeet}
                                                onChange={(e) => setHeightFeet(e.target.value)}
                                                className={styles.feetInput}
                                                step={1}
                                                max={8}
                                            />
                                            <span className={styles.unitLabel}>ft</span>
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <input
                                                type="number"
                                                value={heightInches}
                                                onChange={(e) => setHeightInches(e.target.value)}
                                                className={styles.inchesInput}
                                                step={1}
                                                max={11}
                                                min={0}
                                            />
                                            <span className={styles.unitLabel}>in</span>
                                        </div>

                                    </div>

                                </div>

                                <div className={styles.inputGroup}>

                                    <label className={styles.label}>Weight</label>
                                    
                                    <div className={styles.weightInputContainer}>
                                        <div className={styles.inputWrapper}>
                                            <input
                                                type="number"
                                                value={weight}
                                                onChange={(e) => setWeight(e.target.value)}
                                                className={styles.weightInput}
                                                step={1}
                                                min={1}
                                                max={1000}
                                            />
                                            <span className={styles.unitLabel}>lbs</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {bmi && (
                                <div className={styles.bmiContainer}>
                                    <h3 className={styles.bmiTitle}>Your BMI {bmi}</h3>
                                    <span className={styles.bmiCategory}>{bmiCategory}</span>
                                    <span className={styles.bmiInfo}>
                                        BMI is used to determine program eligibility. Standard programs require BMI ≥27, modified programs are available for BMI 25-27, and maintenance options for BMI 24+. Our clinicians will review your complete health profile during consultation.
                                    </span>
                                </div>
                            )}

                            <div className={styles.confirmationButtons}>
                                <button 
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                    type='button'
                                >
                                    Back
                                </button>
                                <button
                                    type='submit'
                                    className={`${styles.primaryButton} ${!bmi ? styles.disabledButton : ''}`}
                                    onClick={handleConfirm}
                                >
                                    Continue
                                </button>
                            </div>

                        </form>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
